import logger from './logger';

/**
 * API Error class for consistent error structure
 */
export class APIError extends Error {
  constructor(status, message, details = null, originalError = null) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.details = details;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Convert error to JSON representation
   * @returns {Object} Error object
   */
  toJSON() {
    return {
      name: this.name,
      status: this.status,
      message: this.message,
      details: this.details,
      timestamp: this.timestamp
    };
  }

  /**
   * Check if error is a specific HTTP status
   * @param {number} status - HTTP status code to check
   * @returns {boolean} True if error matches status
   */
  isStatus(status) {
    return this.status === status;
  }

  /**
   * Check if error is a client error (4xx)
   * @returns {boolean} True if client error
   */
  isClientError() {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Check if error is a server error (5xx)
   * @returns {boolean} True if server error
   */
  isServerError() {
    return this.status >= 500 && this.status < 600;
  }

  /**
   * Check if error is a network error
   * @returns {boolean} True if network error
   */
  isNetworkError() {
    return this.status === 0;
  }
}

/**
 * Network Error class for connection issues
 */
export class NetworkError extends APIError {
  constructor(message = 'Network error - unable to reach the server', originalError = null) {
    super(0, message, { type: 'network' }, originalError);
    this.name = 'NetworkError';
  }
}

/**
 * Validation Error class for input validation failures
 */
export class ValidationError extends APIError {
  constructor(message, validationErrors = [], originalError = null) {
    super(422, message, { validationErrors }, originalError);
    this.name = 'ValidationError';
  }
}

/**
 * Authentication Error class for auth failures
 */
export class AuthenticationError extends APIError {
  constructor(message = 'Authentication failed', originalError = null) {
    super(401, message, { type: 'authentication' }, originalError);
    this.name = 'AuthenticationError';
  }
}

/**
 * Authorization Error class for permission failures
 */
export class AuthorizationError extends APIError {
  constructor(message = 'Access denied - insufficient permissions', originalError = null) {
    super(403, message, { type: 'authorization' }, originalError);
    this.name = 'AuthorizationError';
  }
}

/**
 * Rate Limiting Error class for rate limit exceeded
 */
export class RateLimitError extends APIError {
  constructor(message = 'Rate limit exceeded. Please try again later.', retryAfter = null, originalError = null) {
    super(429, message, { retryAfter }, originalError);
    this.name = 'RateLimitError';
  }
}

/**
 * Handle and transform axios errors into consistent APIError instances
 * @param {Error} error - Original error from axios
 * @param {string} context - Context where error occurred (for logging)
 * @returns {APIError} Standardized API error
 */
export const handleApiError = (error, context = 'API Request') => {
  // Log the original error for debugging
  logger.error(`${context} failed`, {
    message: error.message,
    status: error.response?.status,
    url: error.config?.url,
    method: error.config?.method
  });

  // Handle different error types
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    const message = data?.message || data?.error || `HTTP ${status} Error`;
    const details = data?.errors || data?.details || null;

    switch (status) {
      case 400:
        const validationError400 = new ValidationError(message, details, error);
        validationError400.response = error.response;
        return validationError400;
      case 401:
        const authError = new AuthenticationError(message, error);
        authError.response = error.response;
        return authError;
      case 403:
        const authzError = new AuthorizationError(message, error);
        authzError.response = error.response;
        return authzError;
      case 404:
        const notFoundError = new APIError(404, 'Resource not found', { url: error.config?.url }, error);
        notFoundError.response = error.response;
        return notFoundError;
      case 422:
        const validationError422 = new ValidationError(message, details, error);
        validationError422.response = error.response;
        return validationError422;
      case 429:
        const retryAfter = error.response.headers['retry-after'];
        const rateLimitError = new RateLimitError(message, retryAfter, error);
        rateLimitError.response = error.response;
        return rateLimitError;
      case 500:
        const serverError = new APIError(500, 'Internal server error', details, error);
        serverError.response = error.response;
        return serverError;
      default:
        const apiError = new APIError(status, message, details, error);
        apiError.response = error.response;
        return apiError;
    }
  } else if (error.request) {
    // Network error - no response received
    return new NetworkError('Network error. Please check your connection.', error);
  } else {
    // Request setup error
    return new APIError(0, 'Request configuration error', { originalMessage: error.message }, error);
  }
};

/**
 * Retry mechanism for API requests
 * @param {Function} apiCall - Function that makes the API call
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries
 * @param {number} options.baseDelay - Base delay between retries in ms
 * @param {Function} options.shouldRetry - Function to determine if error should be retried
 * @returns {Promise} Promise that resolves to API response or rejects with final error
 */
export const withRetry = async (apiCall, options = {}) => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    shouldRetry = (error) => error.isServerError() || error.isNetworkError()
  } = options;

  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error instanceof APIError ? error : handleApiError(error);
      
      if (attempt === maxRetries || !shouldRetry(lastError)) {
        break;
      }
      
      const delay = baseDelay * Math.pow(2, attempt);
      logger.debug(`Retrying API call in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`, {
        error: lastError.message,
        status: lastError.status
      });
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Timeout wrapper for API calls
 * @param {Promise} promise - Promise to add timeout to
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} operation - Operation name for error message
 * @returns {Promise} Promise that rejects if timeout is exceeded
 */
export const withTimeout = (promise, timeoutMs = 10000, operation = 'API call') => {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new NetworkError(`${operation} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    })
  ]);
};

/**
 * Validate required parameters
 * @param {Object} params - Parameters to validate
 * @param {string[]} required - Required parameter names
 * @param {string} context - Context for error message
 * @throws {ValidationError} If required parameters are missing
 */
export const validateRequiredParams = (params, required, context = 'API call') => {
  const missing = required.filter(param => 
    params[param] === undefined || 
    params[param] === null || 
    params[param] === ''
  );

  if (missing.length > 0) {
    throw new ValidationError(
      `${context}: Missing required parameters`,
      missing.map(param => ({ field: param, message: 'This field is required' }))
    );
  }
};

/**
 * Create error handler middleware for consistent error processing
 * @param {string} context - Context for error logging
 * @returns {Function} Error handler function
 */
export const createErrorHandler = (context) => {
  return (error) => {
    const apiError = error instanceof APIError ? error : handleApiError(error, context);
    throw apiError;
  };
};

/**
 * Extract user-friendly error message for UI display
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message  
 */
export const getUserFriendlyMessage = (error) => {
  if (error instanceof NetworkError) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  if (error instanceof AuthenticationError) {
    return 'Your session has expired. Please log in again.';
  }
  
  if (error instanceof AuthorizationError) {
    return 'You do not have permission to perform this action.';
  }
  
  if (error instanceof ValidationError) {
    return error.details?.validationErrors?.length > 0 
      ? 'Please check your input and try again.'
      : error.message;
  }
  
  if (error instanceof RateLimitError) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  
  if (error instanceof APIError) {
    if (error.isServerError()) {
      return 'A server error occurred. Please try again later.';
    }
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};